import React, { useState } from 'react';
import classNames from 'classnames';
import ProductThumbnailSpinner from './product-thumbnail-loading-spinner';
import {
  foldedStyles,
  cornerStyles,
  foldedSrc,
  roundedSrc,
  maskSrc
} from './product-thumbnails-styles-helper';
import screenAndStyle from './screen-and-style';
import { getViewport } from '../_utils/hoc/with-viewport-size';

// -----------------------------------------------------------
//  NewProductThumbnail
// -----------------------------------------------------------
const NewProductThumbnail = ({
  thumbnails,
  imageIndex,
  isLoading,
  isCard,
  children,
  roundedCorners,
  foldedCard,
  scallopedEdges,
  elegantCorners,
  squareEdges,
  altTag,
}) => {
  const [values, setValues] = useState({
    srcUrl: thumbnails[imageIndex],
    loading: isLoading,
    dimensions: { width: null, height: null },
    ratio: null,
    orientation: null,
    isMobile: getViewport().isMobile,
  });
  const cornerType = (() => {
    if (scallopedEdges) {
      return 'scalloped';
    }
    if (elegantCorners) {
      return 'elegant';
    }
    return null;
  })();
  const previewIndex = React.useRef(imageIndex);
  const srcRef = React.useRef();
  const minHeight = React.useRef();
  const intervalRef = React.useRef(); //create a ref for interval
  const over = React.useRef(false);
  const styleClasses =
    screenAndStyle.isSafari() &&
    screenAndStyle.isMobileBrowser() &&
    roundedCorners
      ? 'double-background-image-edge'
      : '';

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted) { // to prevent state update in unmounted component
      setValues({ ...values, loading: true });
      const img = new Image();
      img.src = thumbnails[imageIndex];
      img.onload = () => {
        const ratio = (img.width / img.height).toFixed(2);
        minHeight.current = ratio > 1 ? img.width : img.height;
        srcRef.current = src(ratio);
        if (isMounted) { // to prevent state update in unmounted component
          setValues(prevValues => {
            return {
              ...prevValues,
              loading: false,
              srcUrl: thumbnails[imageIndex],
              dimensions: { width: img.width, height: img.height },
              ratio: ratio,
              orientation: ratio > 1 ? 'landscape' : 'portrait',
            };
          });
        }
      }
      return () => { isMounted = false };
    };
    img.onerror = () => {
      setValues({ ...values, loading: false });
    };
  }, [imageIndex]);

  function src(ratio) {
    if (foldedCard) {
      return foldedSrc(ratio);
    }

    if (roundedCorners) {
      return roundedSrc(ratio);
    }

    if (scallopedEdges || elegantCorners) {
      return maskSrc(cornerType, ratio);
    }
  }

  function handleMouseEnter() {
    if ((thumbnails || []).length < 2) {
      return;
    }

    over.current = true;

    setTimeout(() => debouncedHandler(), 200);
    async function debouncedHandler() {
      if (!values.loading) {
        const promises = [];

        setValues(prevValues => {
          return { ...prevValues, loading: true };
        });
        thumbnails.forEach(thumbnail => {
          promises.push(
            new Promise((resolve, reject) => {
              const image = new Image();

              image.onload = function() {
                resolve();
              };

              image.src = thumbnail;
            })
          );
        });
        await Promise.all(promises);
        onLoaded();
      }
    }
    function onLoaded() {
      setValues(prevValues => {
        return { ...prevValues, loading: false };
      });

      if (over.current) {
        start();
      }
    }
  }

  function start() {
    load(previewIndex.current);
    intervalRef.current = setInterval(() => {
      load();
    }, 800);
  }

  function stop() {
    clearInterval(intervalRef.current);
    load(0);
  }

  function handleMouseLeave() {
    over.current = false;
    setValues(prevValues => {
      return { ...prevValues, loading: false };
    });
    stop();
  }

  function load(newPreviewIndex) {
    if (typeof newPreviewIndex === 'undefined') {
      newPreviewIndex = previewIndex.current + 1;
    }
    const index = newPreviewIndex % thumbnails.length;
    const image = new Image();

    image.onload = function() {
      previewIndex.current = index;
      const ratio = (image.width / image.height).toFixed(2);
      srcRef.current = src(ratio);
      setValues(prevValues => {
        return {
          loading: false,
          srcUrl: thumbnails[index],
          dimensions: { width: image.width, height: image.height },
          ratio,
          orientation: ratio > 1 ? 'landscape' : 'portrait',
        };
      });
    };
    image.src = thumbnails[index];
  }

  function renderOverlay() {
    if (roundedCorners || foldedCard || scallopedEdges || elegantCorners) {
      return (
        <img
          className="overlay img-fluid"
          alt=""
          src={srcRef.current}
          style={
            foldedCard
              ? foldedStyles(values.ratio)
              : cornerStyles(values.ratio)
          }
        />
      );
    }
  }

  return (
    <div
      className={classNames([
        '_thumbnail-outer d-flex m-auto align-items-center',
        {
          'shadowing-for-cards': isCard || squareEdges,
          loading: values.loading,
        },
      ])}
      style={{minHeight: minHeight.current}}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-test-id={
        roundedCorners || foldedCard || scallopedEdges || elegantCorners
          ? 'product-thumbnail-outer'
          : 'product-thumbnail-is-card'
      }>
      <div className="align-content-center thumbnail-div d-flex align-items-center" style={{maxHeight: 'inherit'}}>
        {renderOverlay()}
        <img
          src={values.srcUrl}
          className={classNames(
            'product-thumbnail img-fluid',
            { 'folded-card': foldedCard },
            { orientation: foldedCard },
            styleClasses
          )}
          alt={altTag}
          data-test-id="product-thumbnail-div"
        />
      </div>
      <ProductThumbnailSpinner />
      {children}
    </div>
  );
};

export default NewProductThumbnail;
